import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import type {
  ApplicationStatusResult,
  YardiApplicantRow,
  YardiCompanyOption,
  YardiGuestCardApplication,
  YardiGuestInviteSendRequest,
  YardiGuestInviteValidateRequest,
  YardiGuestInviteValidateResponse,
  YardiInviteListApiResponse,
  YardiPropertyOption,
} from '../types'

function unwrapArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as { data: T[] }).data)) {
    return (data as { data: T[] }).data
  }
  return []
}

/** List: GET /api/all_company — Angular omits yardi flag */
export async function fetchAllCompanyList(params: {
  q?: string
  org_id?: string
}): Promise<YardiCompanyOption[]> {
  const p = new URLSearchParams()
  if (params.q) p.append('q', params.q)
  if (params.org_id) p.append('org_id', params.org_id)
  const res = await apiClient.get<YardiCompanyOption[]>(`/api/all_company?${p.toString()}`)
  return unwrapArray(res.data)
}

/** List: GET /api/all_property */
export async function fetchAllPropertyList(params: {
  q?: string
  company_id?: string
  org_id?: string
}): Promise<YardiPropertyOption[]> {
  const p = new URLSearchParams()
  if (params.q) p.append('q', params.q)
  if (params.company_id) p.append('company_id', params.company_id)
  if (params.org_id) p.append('org_id', params.org_id)
  const res = await apiClient.get<YardiPropertyOption[]>(`/api/all_property?${p.toString()}`)
  return unwrapArray(res.data)
}

/** Guest search: yardi only, no listed in Angular loadProperty initial */
export async function fetchGuestSearchProperties(
  q: string | undefined,
  companyId?: string,
  orgId?: string,
): Promise<YardiPropertyOption[]> {
  const p = new URLSearchParams({ yardi: 'true' })
  if (q) p.append('q', q)
  if (companyId) p.append('company_id', companyId)
  if (orgId) p.append('org_id', orgId)
  const res = await apiClient.get<YardiPropertyOption[]>(`/api/all_property?${p.toString()}`)
  return unwrapArray(res.data)
}

export async function fetchGuestSearchCompanies(q: string | undefined, orgId?: string): Promise<YardiCompanyOption[]> {
  const p = new URLSearchParams({ yardi: 'true' })
  if (q) p.append('q', q)
  if (orgId) p.append('org_id', orgId)
  const res = await apiClient.get<YardiCompanyOption[]>(`/api/all_company?${p.toString()}`)
  return unwrapArray(res.data)
}

export async function postYardiManualInvite(body: Record<string, unknown>): Promise<number> {
  const res = await apiClient.post<number>('/api/yardi_manual_invite', body)
  const data = res.data as unknown
  if (typeof data === 'number') return data
  if (data && typeof data === 'object' && 'data' in data && typeof (data as { data: number }).data === 'number') {
    return (data as { data: number }).data
  }
  return Number(data)
}

export async function postYardiGuestInviteValidate(
  body: YardiGuestInviteValidateRequest,
): Promise<YardiGuestInviteValidateResponse> {
  const res = await apiClient.post<YardiGuestInviteValidateResponse>(
    API_ENDPOINTS.YARDI_GUEST_INVITE_VALIDATE,
    body,
  )
  const data = res.data as unknown
  // PHP json_success returns the validate payload directly; tolerate a nested { data } shape.
  if (data && typeof data === 'object' && 'valid' in data) {
    return data as YardiGuestInviteValidateResponse
  }
  if (data && typeof data === 'object' && 'data' in data) {
    return (data as { data: YardiGuestInviteValidateResponse }).data
  }
  return data as YardiGuestInviteValidateResponse
}

export async function postYardiGuestInviteSend(body: YardiGuestInviteSendRequest): Promise<number> {
  const res = await apiClient.post<number>(API_ENDPOINTS.YARDI_GUEST_INVITE_SEND, body)
  const data = res.data as unknown
  if (typeof data === 'number') return data
  if (data && typeof data === 'object' && 'data' in data && typeof (data as { data: number }).data === 'number') {
    return (data as { data: number }).data
  }
  return Number(data)
}

const DEFAULT_APPLICATION_STATUS_POLL_MS = 3000

/** Poll until the yardi application leaves INQUEUE (same pattern as invite list retry). */
export async function pollApplicationStatusUntilSettled(
  applicationId: number,
  intervalMs = DEFAULT_APPLICATION_STATUS_POLL_MS,
): Promise<ApplicationStatusResult> {
  while (true) {
    const result = await getApplicationStatus(applicationId)
    if (result?.state !== 'INQUEUE') {
      return result
    }
    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs)
    })
  }
}

export async function getApplicationStatus(applicationId: number): Promise<ApplicationStatusResult> {
  const res = await apiClient.get<ApplicationStatusResult>(`/api/check_application_status/${applicationId}`)
  return res.data as ApplicationStatusResult
}

export async function fetchYardiInviteList(
  params: Record<string, string | number | boolean | string[] | undefined>,
): Promise<YardiInviteListApiResponse> {
  const p = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '') return
    if (Array.isArray(v)) {
      const arrayKey = k.endsWith('[]') ? k : `${k}[]`
      v.forEach((item) => p.append(arrayKey, String(item)))
      return
    }
    p.append(k, String(v))
  })
  const res = await apiClient.get<YardiInviteListApiResponse>(`/api/yardi_invite?${p.toString()}`)
  return res.data as YardiInviteListApiResponse
}

/**
 * Angular getRequestBody: `inviteStatus` array → key `inviteStatus[]` for HttpClient params.
 */
export function applyInviteStatusArrayKey(
  raw: Record<string, string | number | boolean | string[] | undefined>,
): Record<string, string | number | boolean | string[] | undefined> {
  const next = { ...raw }
  if (Array.isArray(next.inviteStatus)) {
    const arr = next.inviteStatus
    delete next.inviteStatus
    next['inviteStatus[]'] = arr
  }
  return next
}

export async function postYardiApplicationRetry(applicationId: number): Promise<void> {
  await apiClient.post(`/api/yardi_application/${applicationId}`)
}

export async function searchGuestCards(
  params: Record<string, string | number | boolean>,
): Promise<YardiGuestCardApplication[]> {
  const p = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '') return
    p.append(k, String(v))
  })
  const res = await apiClient.get<YardiGuestCardApplication[]>(
    `${API_ENDPOINTS.SEARCH_GUEST_CARDS}?${p.toString()}`,
  )
  return unwrapArray(res.data)
}

export async function fetchYardiApplicantsList(applicationId: number): Promise<YardiApplicantRow[]> {
  const res = await apiClient.get<YardiApplicantRow[]>('/api/yardi_applicants_list', {
    params: { applicationId },
  })
  return unwrapArray(res.data)
}
